import { useState } from 'react';
import {
  Color, EMERGENCY_CONTACT_LIMIT, MY_EMERGENCY_CONTACTS_PATH, Size, emergencyContactProblems, emergencyContactsReadPath, emergencyReasonProblem, formatPhone, t,
  type EmergencyContactDraft, type EmergencyContactRow, type EmergencyContactsAnswer,
} from '@bms2/shared';
import { Button, PhoneInput, TextInput, Textarea } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { post } from '../records/api';
import styles from './HrPages.module.css';

/** The contacts as a list: name, relationship and the number in the one phone format. */
function ContactList({ contacts }: { contacts: readonly EmergencyContactRow[] }) {
  if (contacts.length === 0) return <p className={styles.meta}>{t('hr.emergency.none')}</p>;
  return (
    <ul className={styles.list}>
      {contacts.map((row) => (
        <li key={row.id} className={styles.item} data-emergency-contact={row.rank}>
          <span className={styles.itemMain}>
            <span>{row.name}</span>
            <span className={styles.meta}>{[row.relationship, formatPhone(row.phoneNumber)].join(' · ')}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The reason form (decision 3184): a manager in the chain gives one sentence
 * before the contacts are answered. 🔒 The reason is kept with the read and HR
 * is told of it; the window says so before the read is made.
 */
function ReasonWindow({ apiBaseUrl, employmentId, onClose, onRead }: { apiBaseUrl: string; employmentId: string; onClose: () => void; onRead: (contacts: EmergencyContactRow[]) => void }) {
  const [reason, setReason] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const problem = emergencyReasonProblem(reason);
  const read = async () => {
    const answer = await post<EmergencyContactsAnswer>(emergencyContactsReadPath(employmentId), { reason }, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onRead(answer.data.contacts);
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.emergency.reasonTitle')}
      width="regular"
      dataForm="emergency-reason"
      typed={reason !== ''}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.emergency.reasonAct'), onAct: () => read(), disabled: problem !== null, disabledReason: problem ?? '' }}
    >
      <div className={styles.fields} data-emergency-reason={employmentId}>
        <p className={styles.meta}>{t('hr.emergency.reasonLead')}</p>
        <Textarea size={Size.Large} label={t('hr.emergency.reason')} value={reason} onChange={setReason} required rows={3} name="emergencyReason" />
      </div>
    </FormFrame>
  );
}

/** 🔒 The Emergency section as the chain reads it: no rows until the reason form answers them. */
export function EmergencyReasonRead({ apiBaseUrl, employmentId }: { apiBaseUrl: string; employmentId: string }) {
  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState<EmergencyContactRow[] | null>(null);
  return (
    <div className={styles.fields} data-emergency-behind-reason={employmentId}>
      {contacts === null ? (
        <span data-emergency-read="open">
          <Button size={Size.Small} color={Color.Navy} text={[t('hr.emergency.readButton')]} actions={[() => setOpen(true)]} />
        </span>
      ) : (
        <div data-emergency-revealed="true"><ContactList contacts={contacts} /></div>
      )}
      {open ? (
        <ReasonWindow
          apiBaseUrl={apiBaseUrl}
          employmentId={employmentId}
          onClose={() => setOpen(false)}
          onRead={(rows) => {
            setOpen(false);
            setContacts(rows);
          }}
        />
      ) : null}
    </div>
  );
}

type ContactDraft = EmergencyContactDraft & { version?: number };

const EMPTY_CONTACT: ContactDraft = { name: '', relationship: '', phoneNumber: '' };

/** The employee's own edit on My Info: one or two contacts, a second added or removed here. */
function EditContactsWindow({ apiBaseUrl, start, onClose, onSaved }: { apiBaseUrl: string; start: readonly EmergencyContactRow[]; onClose: () => void; onSaved: () => void }) {
  const [drafts, setDrafts] = useState<ContactDraft[]>(start.length > 0 ? start.map((row) => ({ name: row.name, relationship: row.relationship, phoneNumber: row.phoneNumber, version: row.version })) : [EMPTY_CONTACT]);
  const [typed, setTyped] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const change = (index: number, next: ContactDraft) => {
    setTyped(true);
    setDrafts(drafts.map((row, at) => (at === index ? next : row)));
  };
  const problems = emergencyContactProblems(drafts);
  const save = async () => {
    // 🔨 A contact the form held and the save drops names the version it read it at (T2284).
    const removed = start.filter((row) => row.rank > drafts.length).map((row) => ({ rank: row.rank, version: row.version }));
    const answer = await post<EmergencyContactsAnswer>(MY_EMERGENCY_CONTACTS_PATH, { contacts: drafts, removed }, apiBaseUrl);
    if (!answer.ok) return setRefusal(answer.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('hr.emergency.editTitle')}
      width="wide"
      dataForm="emergency-contacts"
      typed={typed}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('hr.emergency.editAct'), onAct: () => save(), disabled: problems.length > 0 || !typed, disabledReason: problems[0] ?? '' }}
    >
      <div className={styles.fields} data-emergency-edit="true">
        <p className={styles.meta}>{t('hr.emergency.editLead')}</p>
        {drafts.map((draft, index) => (
          <section key={index} className={styles.section} data-emergency-draft={index + 1}>
            <h3 className={styles.heading}>{t(index === 0 ? 'hr.emergency.first' : 'hr.emergency.second')}</h3>
            <div className={styles.controls}>
              <TextInput size={Size.Medium} label={t('hr.emergency.name')} value={draft.name} onChange={(name) => change(index, { ...draft, name })} required name={`emergencyName${index + 1}`} />
              <TextInput size={Size.Medium} label={t('hr.emergency.relationship')} value={draft.relationship} onChange={(relationship) => change(index, { ...draft, relationship })} required name={`emergencyRelationship${index + 1}`} />
              <PhoneInput size={Size.Medium} label={t('hr.emergency.phone')} value={draft.phoneNumber} onChange={(phoneNumber) => change(index, { ...draft, phoneNumber })} required name={`emergencyPhone${index + 1}`} />
            </div>
            {index === 1 ? (
              <Button size={Size.Small} color={Color.Inactive} text={[t('hr.emergency.remove')]} actions={[() => { setTyped(true); setDrafts(drafts.slice(0, 1)); }]} />
            ) : null}
          </section>
        ))}
        {drafts.length < EMERGENCY_CONTACT_LIMIT ? (
          <span data-emergency-add="true">
            <Button size={Size.Small} color={Color.Navy} text={[t('hr.emergency.add')]} actions={[() => { setTyped(true); setDrafts([...drafts, EMPTY_CONTACT]); }]} />
          </span>
        ) : null}
      </div>
    </FormFrame>
  );
}

/** 🔒 The Emergency section on My Info: the employee keeps their own contacts there (decision 3184). */
export function EmergencyContactsKeeper({ apiBaseUrl, contacts, onSaved }: { apiBaseUrl: string; contacts: readonly EmergencyContactRow[]; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.controls} data-emergency-keeper="true">
      <span data-emergency-edit-open="true">
        <Button size={Size.Small} color={Color.Navy} text={[t(contacts.length === 0 ? 'hr.emergency.addFirst' : 'hr.emergency.editButton')]} actions={[() => setOpen(true)]} />
      </span>
      {open ? (
        <EditContactsWindow
          apiBaseUrl={apiBaseUrl}
          start={contacts}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false);
            onSaved();
          }}
        />
      ) : null}
    </div>
  );
}
